<template>
  <myDialog
    :isDialog="isDialog"
    title="案件受理审核"
    :width="'800px'"
    @handleClose="handleClose"
  >
    <template #content>
      <el-form :model="formData" :rules="rules" ref="formRef" label-width="120px">
        <!-- 受理意见（字典：传 acceptOpinionCode + acceptOpinionName） -->
        <el-form-item label="受理意见" prop="acceptOpinionCode" required>
          <el-select
            v-model="formData.acceptOpinionCode"
            placeholder="请选择受理意见"
            style="width: 100%"
            @change="handleOpinionChange"
          >
            <el-option
              v-for="item in acceptOpinionOptions"
              :key="item.dictValue"
              :label="item.dictLabel"
              :value="item.dictValue"
            />
          </el-select>
        </el-form-item>

        <!-- 受理日期：予以受理时选；不予受理时也为该日期 -->
        <el-form-item v-if="isAcceptRequired || isRejectRequired" label="受理日期" prop="acceptData" required>
          <el-date-picker
            v-model="formData.acceptData"
            type="date"
            placeholder="请选择年/月/日"
            value-format="yyyy-MM-dd 00:00:00"
            style="width: 100%"
          />
        </el-form-item>

        <!-- 受理人（当前用户，提交传 acceptUserId、acceptUserName） -->
        <el-form-item label="受理人" prop="acceptUserName">
          <el-input v-model="formData.acceptUserName" disabled />
        </el-form-item>

        <!-- 受理说明 -->
        <el-form-item label="受理说明" prop="acceptExplain">
          <el-input
            v-model="formData.acceptExplain"
            type="textarea"
            placeholder="请输入受理说明 (可选)"
            :rows="4"
            resize="vertical"
          />
        </el-form-item>
      </el-form>
    </template>
    <template #footer>
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitLoading">提交</el-button>
    </template>
  </myDialog>
</template>

<script>
import { getListByDictType } from '@/api/sys/dictData'
import { mapGetters } from 'vuex'

export default {
  name: 'AcceptDialog',
  props: {
    isDialog: {
      type: Boolean,
      default: false
    },
    rowData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      submitLoading: false,
      acceptOpinionOptions: [],
      formData: {
        acceptOpinionCode: '',
        acceptOpinionName: '',
        acceptData: '',
        acceptUserId: '',
        acceptUserName: '',
        acceptExplain: ''
      },
      rules: {
        acceptOpinionCode: [
          { required: true, message: '请选择受理意见', trigger: 'change' }
        ],
        acceptData: [
          {
            validator: (rule, value, callback) => {
              if ((this.isAcceptRequired || this.isRejectRequired) && !value) {
                callback(new Error('请选择受理日期'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['name']),
    // 当受理意见为「予以受理」或「受理」时显示受理日期
    isAcceptRequired() {
      const opt = this.acceptOpinionOptions.find(
        o => String(o.dictValue) === String(this.formData.acceptOpinionCode)
      )
      const label = (opt?.dictLabel || '').trim()
      if (label === '予以受理' || label === '受理') return true
      if (!this.formData.acceptOpinionCode) return false
      if (!opt && (this.formData.acceptOpinionCode === '1' || this.formData.acceptOpinionCode === 1)) return true
      return false
    },
    // 当受理意见为「不予受理」时也显示受理日期
    isRejectRequired() {
      const opt = this.acceptOpinionOptions.find(
        o => String(o.dictValue) === String(this.formData.acceptOpinionCode)
      )
      const label = (opt?.dictLabel || '').trim()
      return label === '不予受理'
    }
  },
  watch: {
    isDialog(val) {
      if (val) {
        this.initCurrentUser()
        // 先确保字典已加载再重置表单，这样默认「予以受理」能正确显示受理日期
        this.loadAcceptOpinionDict().then(() => {
          this.resetForm()
        })
      }
    }
  },
  created() {
    this.loadAcceptOpinionDict()
  },
  methods: {
    // 加载审核意见字典（返回 Promise 便于在 watch 中等待）
    loadAcceptOpinionDict() {
      return getListByDictType('industraial_injury_accept')
        .then(res => {
          if (res && res.code === 200 && res.data) {
            this.acceptOpinionOptions = res.data
          } else {
            this.acceptOpinionOptions = [
              { dictValue: '1', dictLabel: '予以受理' },
              { dictValue: '0', dictLabel: '不予受理' }
            ]
          }
        })
        .catch(e => {
          console.warn('审核意见字典加载失败，使用默认选项', e)
          this.acceptOpinionOptions = [
            { dictValue: '1', dictLabel: '予以受理' },
            { dictValue: '0', dictLabel: '不予受理' }
          ]
        })
    },
    // 初始化受理人：从 store 取 userId、name，提交时传 acceptUserId、acceptUserName
    initCurrentUser() {
      const state = this.$store.state.user || {}
      this.formData.acceptUserId = state.userId || ''
      this.formData.acceptUserName = this.name || state.name || '当前用户'
    },
    // 受理意见变化时清空受理日期
    handleOpinionChange() {
      this.formData.acceptData = ''
      this.$nextTick(() => {
        this.$refs.formRef?.clearValidate(['acceptData'])
      })
    },
    handleClose() {
      this.$emit('handleClose')
      this.resetForm()
    },
    resetForm() {
      this.$refs.formRef?.resetFields()
      const defaultCode = this.getDefaultAcceptOpinionValue()
      this.formData = {
        acceptOpinionCode: defaultCode,
        acceptOpinionName: '',
        acceptData: '',
        acceptUserId: '',
        acceptUserName: '',
        acceptExplain: ''
      }
      this.initCurrentUser()
    },
    // 默认选中「予以受理」的 dictValue
    getDefaultAcceptOpinionValue() {
      const acceptOpt = this.acceptOpinionOptions.find(o => (o.dictLabel || '').trim() === '予以受理')
      return acceptOpt ? acceptOpt.dictValue : (this.acceptOpinionOptions[0]?.dictValue || '1')
    },
    handleSubmit() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return

        if ((this.isAcceptRequired || this.isRejectRequired) && !this.formData.acceptData) {
          this.$message.warning('请选择受理日期')
          return
        }

        const opt = this.acceptOpinionOptions.find(
          o => String(o.dictValue) === String(this.formData.acceptOpinionCode)
        )
        const acceptOpinionName = (opt && opt.dictLabel) || this.formData.acceptOpinionName || ''

        this.submitLoading = true
        const submitData = {
          ...this.rowData,
          acceptOpinionCode: this.formData.acceptOpinionCode,
          acceptOpinionName,
          acceptData: this.formData.acceptData,
          acceptUserId: this.formData.acceptUserId,
          acceptUserName: this.formData.acceptUserName,
          acceptExplain: this.formData.acceptExplain
        }
        this.$emit('submit', submitData)
      })
    },
    // 由父组件在接口成功后调用，关闭 loading 并关闭弹窗
    finishSubmit() {
      this.submitLoading = false
      this.handleClose()
    },
    // 由父组件在接口失败时调用
    failSubmit() {
      this.submitLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ .el-form-item__label {
  color: #2a3347 !important;
  font-weight: 400;
  font-size: 16px;
}

/deep/ .el-dialog__header {
  background: #297dee !important;
}

/deep/ .el-dialog__title {
  color: #ffffff !important;
  font-weight: 700;
  font-size: 17px;
}

/deep/ .el-dialog__headerbtn {
  filter: brightness(0) invert(1) !important;
}
</style>
